import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Slide from '@mui/material/Slide'
import { type Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { type SystemStyleObject } from '@mui/system'
import theme from 'assets/theme'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import {
  KNArrowDownIcon,
  KNCompanyIcon,
  KNExternalURLIcon,
  KNHideMenuIcon,
  KNInvoiceIcon,
  KNRevealIcon,
  KNUserIcon,
} from 'components/KNIcon/KNIcon'
import type { Company } from 'context/authentication/Company.types'
import { getSelectedCompany, getStartPage, setSelectedCompany } from 'context/authentication/User.helpers'
import { UserContext } from 'context/authentication/UserContext'
import { useMenuContext } from 'context/menu/MenuContext'
import { getActiveMenu } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import useSuppressFlag from 'global/hooks/useSuppressFlag'
import { type ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom'
import { getMenuOptions, getUserMenuOptions, type SectionOption } from './DrawerMenu.helpers'

const DrawerMenu = (): ReactElement => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, logoutUser } = useContext(UserContext)
  const [menuState, dispatch] = useMenuContext()
  const { menuOpen, menuExtended } = menuState.menuContext
  const menuOptions = getMenuOptions()
  const userMenuOptions = getUserMenuOptions()

  const [navigationDialogOpen, setNavigationDialogOpen] = useState(false)
  const { suppressFlagName, isSuppressed } = useSuppressFlag('trade-shift-disclaimer')

  const showCompanySelector = user?.companies
  const companySelectorClickable = user?.companies && user.companies.length > 1

  const location = useLocation()
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  useEffect(() => {
    setActiveMenu(getActiveMenu(location.pathname))
  }, [location])

  // Dropdown Menu
  const [anchorElCompanies, setAnchorElCompanies] = useState<null | HTMLElement>(null)
  const [anchorElUserMenu, setAnchorElUserMenu] = useState<null | HTMLElement>(null)
  const companyMenuOpen = Boolean(anchorElCompanies)
  const userMenuOpen = Boolean(anchorElUserMenu)
  const handleClickCompanies = (event: React.MouseEvent<HTMLElement>): void => {
    if (companySelectorClickable) {
      setAnchorElCompanies(event.currentTarget)
    }
  }
  const handleClickUserMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElUserMenu(event.currentTarget)
  }

  const openTradeShiftURL = () => {
    analyticsEvent('polestar_cs_invoice_tradeshift_open')
    window.open('https://go.tradeshift.com/?currentScreen=0', '_blank', 'noopener')
  }

  const handleInvoiceClick = useCallback(() => {
    if (isSuppressed) {
      openTradeShiftURL()
    } else {
      setNavigationDialogOpen(true)
    }
  }, [])

  const handleConfirmationDialogAction = useCallback((): void => {
    setNavigationDialogOpen(false)
    openTradeShiftURL()
  }, [])

  const handleConfirmationDialogClose = useCallback((): void => {
    setNavigationDialogOpen(false)
  }, [])

  const handleClose = useCallback((): void => {
    if (anchorElCompanies) {
      setAnchorElCompanies(null)
    } else if (anchorElUserMenu) {
      setAnchorElUserMenu(null)
    }
  }, [anchorElCompanies, anchorElUserMenu])

  const handleCloseDrawer = (): void => {
    dispatch({
      type: 'setMenuProps',
      payload: {
        menuExtended: menuExtended,
        menuOpen: false,
      },
    })
  }

  const truncateUserDisplayName = (string: string): string => {
    return string.length > 18 ? string.slice(0, 18 - 1) + '...' : string
  }

  const userSection = (sectionOption: SectionOption) => {
    return (
      <Box key={sectionOption.name} display="flex" width="100%">
        <Box
          mx={-1}
          my={0.5}
          sx={{
            width: '8px',
            borderRadius: '0px 4px 4px 0px',
            backgroundColor: activeMenu === 'profile' ? 'background.activeNavRectangle' : null,
          }}
        />
        <Box
          data-guide="menu-profile"
          my={0.5}
          mx={2}
          py={1}
          px={1}
          sx={{
            width: menuExtended ? '92.5%' : '72px',
            display: isMobile ? 'inline-flex' : 'flex',
            cursor: 'pointer',
            alignItems: 'center',
            backgroundPosition: 'center',
            borderRadius: ({ borders }) => borders.borderRadius['lg'],
            backgroundColor: activeMenu === 'profile' ? 'background.activeNavBackground' : null,
            transition: isMobile ? 'all 0.2s ease' : 'none',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'background.contrastBackground',
            },
          }}
          onClick={handleClickUserMenu}
        >
          <KNUserIcon sx={{ mr: 1.5 }}></KNUserIcon>
          {menuExtended && user && (
            <Typography variant="p2">
              {truncateUserDisplayName(user.displayName ?? user.email)}
            </Typography>
          )}
        </Box>
        <Menu
          anchorEl={anchorElUserMenu}
          id="user-menu"
          open={userMenuOpen}
          onClose={handleClose}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
          transformOrigin={{
            horizontal: 'left',
            vertical: 'bottom',
          }}
        >
          <MenuItem
            sx={{
              width: '100%',
            }}
          >
            <Link component={RouterNavLink} to={'/profile'} data-test="drawer-menu-navigate-profile">
              <Typography>
                {t('drawer_menu.show_my_profile')}
              </Typography>
            </Link>
          </MenuItem>
          <MenuItem
            data-test={'logout-button'}
            onClick={(): void => {
              logoutUser()
              analyticsEvent('polestar_logout')
            }}
          >
            <Icon
              sx={({ typography: { size } }: Theme): SystemStyleObject<Theme> => ({
                mr: 0.5,
                fontSize: size.md,
              })}
            >
              logout
            </Icon>
            {t('drawer_menu.logout')}
          </MenuItem>
        </Menu>
      </Box>
    )
  }

  const companyName = (sectionOption: SectionOption) => {
    return (
      <Box key={sectionOption.name} display="flex" width="100%">
        <Box
          mx={-1}
          my={0.5}
          sx={{
            width: '8px',
            borderRadius: '0px 4px 4px 0px',
            backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavRectangle' : null,
          }}
        />
        <Box
          data-guide="menu-company"
          my={0.5}
          mx={2}
          py={1}
          pl={1}
          sx={{
            width: menuExtended ? '92.5%' : '72px',
            display: isMobile ? 'inline-flex' : 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
            alignItems: 'center',
            backgroundPosition: 'center',
            borderRadius: ({ borders }) => borders.borderRadius['lg'],
            backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavBackground' : null,
            transition: isMobile ? 'all 0.2s ease' : 'none',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'background.contrastBackground',
            },
          }}
          onClick={handleClickCompanies}
        >
          <KNCompanyIcon />
          {menuExtended && (
            <Typography variant="p2">
              {getSelectedCompany()?.displayName}
            </Typography>
          )}
          {companySelectorClickable && <KNArrowDownIcon sx={{ height: '15px' }}></KNArrowDownIcon>}
        </Box>
        <Menu
          anchorEl={anchorElCompanies}
          id="company-menu"
          open={companyMenuOpen}
          onClose={handleClose}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          transformOrigin={{
            horizontal: 'center',
            vertical: 'bottom',
          }}
        >
          {user?.companies.map((company, index) => (
            <MenuItem
              key={company.displayName}
              onClick={(): void => handleSelectCompany(company)}
              data-test={`drawer-menu-companySelector-${company.displayName}`}
            >
              <ListItemText disableTypography>{company.displayName}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    )
  }

  const invoices = (sectionOption: SectionOption) => {
    return (
      <Box key={sectionOption.name} display="flex" width="100%">
        <Box
          mx={-1}
          my={0.5}
          sx={{
            width: '8px',
            borderRadius: '0px 4px 4px 0px',
            backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavRectangle' : null,
          }}
        />
        <Box
          my={0.5}
          mx={2}
          py={1}
          px={1}
          sx={{
            width: menuExtended ? '100%' : '40px',
            display: isMobile ? 'inline-flex' : 'flex',
            cursor: 'pointer',
            alignItems: 'center',
            backgroundPosition: 'center',
            borderRadius: ({ borders }) => borders.borderRadius['lg'],
            backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavBackground' : null,
            transition: isMobile ? 'all 0.2s ease' : 'none',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'background.contrastBackground',
            },
          }}
          onClick={handleInvoiceClick}
        >
          <KNInvoiceIcon sx={{ mr: 1.5 }}></KNInvoiceIcon>
          {menuExtended && (<>
              <Typography variant="p2">
                {t(sectionOption.label)}
              </Typography>
              <KNExternalURLIcon sx={{ ml: 1.5, height: '12px', width: '12px' }} />
            </>
          )}

        </Box>
        <KNConfirmationDialog
          message={t('drawer_menu.navigation_warning')}
          skipConfirmationFlag={suppressFlagName}
          open={navigationDialogOpen}
          onAction={handleConfirmationDialogAction}
          onClose={handleConfirmationDialogClose}
        />
      </Box>
    )
  }

  const carrierProfile = (sectionOption: SectionOption) => {
    const sectionOptionLabel: string = sectionOption.label
    return (
      <Link
        component={RouterNavLink}
        key={sectionOption.label}
        to={sectionOption.link}
        onClick={handleCloseDrawer}
        data-test={`drawer-menu-navigate-${sectionOptionLabel}`}
      >
        <Box display="flex" width="100%">
          <Box
            mx={-1}
            my={0.5}
            sx={{
              width: '8px',
              borderRadius: '0px 4px 4px 0px',
              backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavRectangle' : null,
            }} />
          <Box
            my={0.5}
            mx={2}
            py={1}
            px={1}
            sx={{
              width: menuExtended ? '100%' : '40px',
              borderRadius: ({ borders }) => borders.borderRadius['lg'],
              backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavBackground' : null,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              transition: isMobile ? 'all 200ms ease-in-out' : 'none',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'background.contrastBackground',
              },
            }}
          >
            <Icon color="inherit" sx={{ mr: 1.5 }} fontSize="medium">
              {sectionOption.icon}
            </Icon>
            {menuExtended && (
              <>
                <Typography variant="p2" color="inherit" sx={{ textTransform: 'capitalize', marginRight: '8px' }}>
                  {t(sectionOption.label)}
                </Typography>
                <Chip label={t('screens.cs.trip_dashboard.statuses.new')} color={'warning'} size={'small'}></Chip>
              </>
            )}
          </Box>
        </Box>
      </Link>
    )
  }

  const getSection = (sectionOption: SectionOption) => {
    if (sectionOption.name === 'user') {
      return userSection(sectionOption)
    } else if (sectionOption.label === 'company_name' && showCompanySelector) {
      return companyName(sectionOption)
    } else if (sectionOption.name === 'invoices') {
      return invoices(sectionOption)
    } else if (sectionOption.name === 'carrier-profile') {
      return carrierProfile(sectionOption)
    } else {
      const sectionOptionLabel: string = sectionOption.label
      return (
        <Link
          component={RouterNavLink}
          key={sectionOption.label}
          to={sectionOption.link}
          onClick={handleCloseDrawer}
          data-test={`drawer-menu-navigate-${sectionOptionLabel}`}
        >
          <Box display="flex" width="100%">
            <Box
              mx={-1}
              my={0.5}
              sx={{
                width: '8px',
                borderRadius: '0px 4px 4px 0px',
                backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavRectangle' : null,
              }} />
            <Box
              my={0.5}
              mx={2}
              py={1}
              px={1}
              sx={{
                width: menuExtended ? '100%' : '40px',
                borderRadius: ({ borders }) => borders.borderRadius['lg'],
                backgroundColor: activeMenu === sectionOption.name ? 'background.activeNavBackground' : null,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                transition: isMobile ? 'all 200ms ease-in-out' : 'none',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'background.contrastBackground',
                },
              }}
            >
              <Icon color="inherit" sx={{ mr: 1.5 }} fontSize="medium">
                {sectionOption.icon}
              </Icon>
              {menuExtended && (
                <Typography variant="p2" color="inherit" sx={{ textTransform: 'capitalize' }}>
                  {t(sectionOption.label)}
                </Typography>
              )}
            </Box>
          </Box>
        </Link>
      )
    }
  }

  const handleSelectCompany = useCallback((company: Company): void => {
    const previousSelectedCompany = getSelectedCompany()
    setSelectedCompany(company)
    analyticsEvent('polestar_selected_company_changed')
    let reload = false
    // Reload only if we have the same set of permissions
    if (
      previousSelectedCompany &&
      previousSelectedCompany.type === company.type &&
      previousSelectedCompany.role === company.role
    ) {
      reload = true
    }

    if (reload) {
      window.location.reload()
    } else {
      window.location.assign(getStartPage())
    }
  }, [])

  // Drawer Menu
  const handleDrawerMenuExtend = (): void => {
    dispatch({
      type: 'setMenuProps',
      payload: {
        menuOpen: menuOpen,
        menuExtended: !menuExtended,
      },
    })
    if (menuExtended) {
      analyticsEvent('polestar_collapse_side_menu')
    } else {
      analyticsEvent('polestar_expand_side_menu')
    }
  }

  const mobileWrapper = (): JSX.Element => {
    const drawerMenu = (
      <Box
        data-test="drawer-menu"
        sx={({ palette }: Theme): SystemStyleObject<Theme> => ({
          height: '100%',
          backgroundColor: palette.background.default,
          color: palette.primary.main,
          p: 1,
          pt: 3,
          position: 'fixed',
          top: 0,
          width: menuExtended ? '14rem' : '6rem',
          transition: 'width 0.2s',
          right: isMobile ? 0 : null,
          zIndex: isMobile ? 9999 : undefined,
        })}
      >
        <Box sx={{ position: 'relative', height: '100%' }}>
          <Box
            sx={{
              height: '32px',
              display: isMobile ? 'inline-flex' : 'flex',
            }}
          >
            {isMobile && (
              <IconButton
                onClick={handleCloseDrawer}
                data-test={'drawer-menu-extendButton'}
                size={'small'}
                sx={{ right: -10, position: 'absolute' }}
              >
                <MenuOpenRoundedIcon sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
            )}
          </Box>
          <Box>
            {menuOptions.map((section, index) => (
              <Box key={section.sectionName} mb={2.5}>
                <Typography variant="p6" color="primary.main" paddingLeft={2}>
                  {menuExtended && t(section.sectionName)}
                  {!menuExtended && (section.sectionShortName ? t(section.sectionShortName) : t(section.sectionName))}
                </Typography>
                {section.sectionOptions.map((sectionOption) => (
                  getSection(sectionOption)
                ))}
              </Box>
            ))}
          </Box>
          {isMobile && (<Box
            sx={({ functions: { pxToRem } }: Theme): SystemStyleObject<Theme> => ({
              position: 'absolute',
              bottom: isMobile ? 0 : pxToRem(30),
              width: '100%',
            })}
          >
            <Box>
              {userMenuOptions.map((section, index) => (
                <Box key={section.sectionName} mb={2.5}>
                  <Typography variant="p6" color="primary.main" paddingLeft={2}>
                    {menuExtended && t(section.sectionName)}
                    {!menuExtended && (section.sectionShortName ? t(section.sectionShortName) : t(section.sectionName))}
                  </Typography>
                  {section.sectionOptions.map((sectionOption) => (
                    getSection(sectionOption)
                  ))}
                </Box>
              ))}
            </Box>
          </Box>)}
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                display: 'flex',
                justifyContent: menuExtended ? 'flex-end' : 'center',
                left: !menuExtended ? -5 : 0,
              }}
            >
              <IconButton
                onClick={(): void => handleDrawerMenuExtend()}
                data-test={'drawer-menu-extendButton'}
                color="primary"
                size={'small'}
              >
                {menuExtended ? <KNHideMenuIcon /> : <KNRevealIcon sx={{ height: '20px' }} />}
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    )
    if (isMobile) {
      return (
        <Slide direction="left" in={isMobile && menuOpen} unmountOnExit>
          {drawerMenu}
        </Slide>
      )
    }
    return drawerMenu
  }

  return mobileWrapper()
}

export default DrawerMenu
